import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { EnviarArpCodigoRequest, ModeloEnvio } from "./utils/phoneValidation";
import { enviarArpCodigo } from "../../services/arpcodigo";
import { useNavigate } from "react-router-dom";
import { usePhoneConfirmationContext } from "../../context/PhoneConfirmationContext";
import { useCountDown } from "./utils/useCountDown";
import { useTranslation } from "react-i18next";

const useHomeController = () => {
  // #region States
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [idCliente, setIdCliente] = useState<string | null>(null);
  const [statusEnvio, setStatusEnvio] = useState<any>(null);
  const captcha = useRef<ReCAPTCHA>(null);
  const [token, setToken] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [isVisibleRecaptcha, setIsVisibleRecaptcha] = useState(true);
  const [modeloEnvioBackup, setModeloEnvioBackup] = useState<ModeloEnvio>(
    ModeloEnvio.SMS,
  );
  // #endregion

  const { t } = useTranslation();

  const handleChangeCaptcha = async (value: any) => {
    try {
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Timeout no reCAPTCHA")), 5000),
      );

      await Promise.race([value, timeout]);
      setToken(value);
    } catch (error) {
      setToken("");
      captcha.current?.reset();
    }
  };

  const handleTextInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMaskedPhone(event.target.value);
  };

  const handleEnviarArpCodigo = async ({
    modeloEnvio,
    eReenvio,
  }: {
    modeloEnvio: ModeloEnvio;
    eReenvio: boolean;
  }): Promise<void> => {
    if (maskedPhone === "") {
      setAlertMessage(t("phoneRequired"));
    } else if (!token) {
      setAlertMessage(t("captchaRequired"));
    } else {
      const body: EnviarArpCodigoRequest = {
        subscriberId: maskedPhone.replace(/\D/g, ""),
        modeloEnvio: modeloEnvio,
        RecaptchaToken: token,
        EReenvio: eReenvio,
      };
      try {
        const resp = await enviarArpCodigo(body);
        if (resp?.status === 200) {
          setStatusEnvio({
            status: resp.data.status,
            enviosRestantes: resp.data.enviosRestantes,
            temCodigoValido: resp.data.temCodigoValido,
            tempoRestanteNovoEnvio: resp.data.tempoRestanteNovoEnvio,
          });
          if (resp.data.enviosRestantes === 0 && !resp.data.temCodigoValido) {
            setAlertMessage(t("resendLimitReached"));
            return;
          }
          setModeloEnvioBackup(modeloEnvio);
          setIdCliente(resp.data.idCliente);
          setAlertMessage(t("codeSentSuccess"));
        } else {
          if (resp?.data.message) {
            setAlertMessage(resp.data.message);
          } else {
            setAlertMessage(t("codeSendError"));
          }
        }
      } catch (error: any) {
        setAlertMessage(t("codeSendError"));
      } finally {
        if (captcha.current) {
          (captcha.current as any).reset();
          setToken("");
        }
      }
    }
  };

  const handleArpCodigoSms = async () => {
    await handleEnviarArpCodigo({
      modeloEnvio: ModeloEnvio.SMS,
      eReenvio: false,
    });
  };

  const handleArpCodigoWhatsapp = async () => {
    await handleEnviarArpCodigo({
      modeloEnvio: ModeloEnvio.WHATSAPP,
      eReenvio: false,
    });
  };

  return {
    alertMessage,
    setAlertMessage,
    idCliente,
    setIdCliente,
    statusEnvio,
    setStatusEnvio,
    handleTextInput,
    handleEnviarArpCodigo,
    handleChangeCaptcha,
    token,
    maskedPhone,
    isVisibleRecaptcha,
    setIsVisibleRecaptcha,
    modeloEnvioBackup,
    handleArpCodigoSms,
    handleArpCodigoWhatsapp,
    captcha,
  };
};

const useResgatarCodigoController = ({
  idCliente,
  handleEnviarArpCodigo,
  statusEnvio,
  setAlertMessage,
  phone,
  token,
  setIsVisibleRecaptcha,
  modeloEnvio,
}: {
  idCliente: string | null;
  handleEnviarArpCodigo: ({
    modeloEnvio,
    eReenvio,
  }: {
    modeloEnvio: ModeloEnvio;
    eReenvio: boolean;
  }) => Promise<void>;
  statusEnvio: any;
  setAlertMessage: (message: string) => void;
  phone?: string;
  token?: string;
  setIsVisibleRecaptcha: (value: boolean) => void;
  modeloEnvio: ModeloEnvio;
}) => {
  const [codigo, setCodigo] = useState<any>("");
  const [fullCode, setFullCode] = useState<boolean>(false);
  const navigate = useNavigate();
  const { validatePhone, success, statusCode } = usePhoneConfirmationContext();
  const { isCounting, startCountdown, countFormatMinSec } = useCountDown();
  const [isSubmit, setIsSubmit] = useState(false);
  const { t } = useTranslation();

  const handleChangeCodigo = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value.replace(/-/g, "").trim();
    setCodigo(inputValue);
    if (inputValue.length === 8) {
      setFullCode(true);
    } else {
      setFullCode(false);
    }
  };

  useEffect(() => {
    startCountdown(statusEnvio?.tempoRestanteNovoEnvio || 10);
  }, [statusEnvio?.tempoRestanteNovoEnvio]);

  const handleRenviarArpCodigo = async () => {
    if (statusEnvio?.enviosRestantes === 0) {
      setIsVisibleRecaptcha(true);
      setAlertMessage(t("resendLimitReached"));
      return;
    }

    if (!token || token === "") {
      setAlertMessage(t("captchaRequired"));
      return;
    }
    await handleEnviarArpCodigo({ modeloEnvio, eReenvio: true });
    startCountdown(statusEnvio?.tempoRestanteNovoEnvio || 10);
  };

  const handleResgataCodigo = async () => {
    try {
      const codigoValida = Object.values(codigo).join("");
      if (/^\d{8}$/.test(codigoValida)) {
        if (idCliente) {
          await validatePhone({
            phone: phone || "",
            IdCliente: idCliente,
            Codigo: codigoValida,
          });
          setIsSubmit(true);
        }
      }
    } catch (error: any) {
      setAlertMessage(t("codeRedeemError"));
    }
  };

  useEffect(() => {
    if (!isSubmit) return;
    if (statusCode == 403) {
      setAlertMessage(t("codeInvalidOrExpired"));
      setIsSubmit(false);
      return;
    }
    if (success) {
      navigate("/success");
    } else if (!success) {
      navigate("/error");
    }
  }, [success, navigate, isSubmit]);

  useEffect(() => {
    if (isCounting) {
      setIsVisibleRecaptcha(false);
      return;
    }
    if (statusEnvio?.enviosRestantes <= 0) {
      setIsVisibleRecaptcha(false);
      return;
    }

    setIsVisibleRecaptcha(true);
  }, [isCounting, statusEnvio?.enviosRestantes, setIsVisibleRecaptcha]);

  return {
    codigo,
    fullCode,
    handleChangeCodigo,
    handleRenviarArpCodigo,
    handleResgataCodigo,
    countFormatMinSec,
    isCounting,
  };
};

export { useHomeController, useResgatarCodigoController };
